import { FiTrash, FiUpload, FiRefreshCw, FiPaperclip } from "react-icons/fi";
import { AspectRatio, Input } from "@chakra-ui/react";

import {
  ChangeEvent,
  ChangeEventHandler,
  MouseEventHandler,
  useRef,
  useState
} from "react";

import truncate from "lodash/truncate";

import { isImageContentType } from "@/helpers/is-image-file";

import { Box, Image, Flex, Text } from "../internal/exports";

import { Button, type IButtonProps } from "../button/button";
import type { FileUploadDataTestIds } from "../question/types";
import {
  resolveTestId,
  scopedTestIds,
  type TestIdProps
} from "../utils/test-ids";

const MAX_LENGTH = 30;

export const DEFAULT_ACCEPTED_FILE_TYPE =
  ".jpg,.jpeg,.gif,.png,.bmp,.zip,.xml,.mp4,.avi,.mpeg,.mov,.flv,.mp3,.wav,.x-wav,.pdf,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.rtf";

export interface FileUploadProps
  extends
    TestIdProps,
    Pick<
      IButtonProps,
      | "id"
      | "size"
      | "onBlur"
      | "children"
      | "disabled"
      | "loadingText"
      | "className"
      | "colorPalette"
      | "aria-label"
    > {
  fileName: string;
  fileSrc: string;
  fileContentType: string;
  accept: string;
  helpText: string;
  deleteText: string;
  replaceText: string;
  deleteDisabled: boolean;
  replaceDisabled: boolean;
  onUpload: ChangeEventHandler<HTMLInputElement>;
  onDelete: MouseEventHandler<HTMLElement>;
  fileUploadDataTestIds?: FileUploadDataTestIds;
}

function FileUpload({
  id,
  size,
  accept,
  onBlur,
  children,
  onUpload,
  onDelete,
  disabled,
  loadingText,
  className,
  colorPalette,
  helpText,
  fileSrc,
  fileName,
  fileContentType,
  deleteText,
  replaceText,
  deleteDisabled,
  replaceDisabled,
  "aria-label": ariaLabel,
  fileUploadDataTestIds,
  ...testIdProps
}: Partial<FileUploadProps>) {
  const [isUploading, setIsUploading] = useState(false);
  const inputFileNode = useRef<HTMLInputElement>(null);

  // Precedence: explicit per-slot prop > derived from root id > legacy default.
  const t = scopedTestIds(resolveTestId(testIdProps));
  const tid = {
    uploadButton:
      fileUploadDataTestIds?.uploadButton ??
      t.child("button") ??
      "fileUpload-button",
    fileInput: fileUploadDataTestIds?.fileInput ?? t.child("input"),
    replaceButton:
      fileUploadDataTestIds?.replaceButton ??
      t.child("replace-button") ??
      "fileUpload-replaceButton",
    deleteButton:
      fileUploadDataTestIds?.deleteButton ??
      t.child("delete-button") ??
      "fileUpload-deleteButton",
    previewImage:
      fileUploadDataTestIds?.previewImage ??
      t.child("image") ??
      "fileUpload-image"
  };

  const handleSelect = () => {
    if (inputFileNode && inputFileNode?.current) inputFileNode.current.click();
  };

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true);
    onUpload && (await onUpload(e));
    setIsUploading(false);
  };

  const displayUploadedFile = () => {
    const imageLikeMime = isImageContentType(fileContentType, fileName);
    return (
      <Box maxWidth={{ lg: "24rem" }}>
        {imageLikeMime && (
          <AspectRatio ratio={16 / 9}>
            <Image
              height={200}
              fallback={<Text>...</Text>}
              alt={fileName}
              borderRadius={8}
              data-testid={tid.previewImage}
              src={fileSrc}
            />
          </AspectRatio>
        )}
        <Flex mt={2} fontSize={size} alignItems="center">
          <FiPaperclip /> &nbsp;
          {truncate(fileName, { length: MAX_LENGTH })}
        </Flex>
        <Flex mt={4} justifyContent="space-between">
          <Button
            variant="tertiary"
            size="sm"
            leftIcon={<FiRefreshCw />}
            onClick={handleSelect}
            data-testid={tid.replaceButton}
            disabled={replaceDisabled}
          >
            {replaceText}
          </Button>
          <Input
            id={id}
            type="file"
            display="none"
            aria-label={ariaLabel}
            ref={inputFileNode}
            onChange={handleUpload}
            accept={accept || DEFAULT_ACCEPTED_FILE_TYPE}
            {...(tid.fileInput ? { "data-testid": tid.fileInput } : {})}
          />
          <Button
            variant="tertiary"
            size="sm"
            leftIcon={<FiTrash />}
            onClick={onDelete}
            data-testid={tid.deleteButton}
            disabled={deleteDisabled}
          >
            {deleteText}
          </Button>
        </Flex>
      </Box>
    );
  };

  return fileSrc && !isUploading ? (
    displayUploadedFile()
  ) : (
    <Flex
      flexDirection={{ base: "column", md: "row" }}
      alignItems={{ lg: "center" }}
    >
      <Button
        variant="secondary"
        id={id}
        size={size}
        display="flex"
        onBlur={onBlur}
        onClick={handleSelect}
        disabled={disabled}
        loading={isUploading}
        data-testid={tid.uploadButton}
        loadingText={loadingText}
        className={className}
        colorPalette={colorPalette}
        aria-label={ariaLabel}
        leftIcon={<FiUpload />}
      >
        {children}
      </Button>
      <Input
        id={id}
        type="file"
        display="none"
        aria-label={ariaLabel}
        ref={inputFileNode}
        onChange={handleUpload}
        accept={accept || DEFAULT_ACCEPTED_FILE_TYPE}
        {...(tid.fileInput ? { "data-testid": tid.fileInput } : {})}
      />
      {helpText && (
        <Text
          id="file-upload"
          ml={{ md: 4 }}
          mt={{ base: 2, md: 0 }}
          dangerouslySetInnerHTML={{ __html: helpText }}
        />
      )}
    </Flex>
  );
}

export { FileUpload };
